import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { downloadBlob } from "@/lib/download";

/**
 * Iter-207 · Bulk Excel upload for dimensions.
 * Reusable component — pass `kind` = "cost-centres" | "business-segments" | "branches".
 * Downloads the template, parses uploaded XLSX/CSV, POSTs to /api/{kind}/bulk-upload.
 */
export default function DimensionBulkUpload({ kind, label, onDone }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/${kind}/template.xlsx`, `zenith_${kind.replace(/-/g, "_")}_template.xlsx`, {});
      toast.success("Template downloaded");
    } catch (e) { toast.error("Template download failed"); }
  };

  const parseXlsx = async (file) => {
    const XLSX = (await import("xlsx")).default || (await import("xlsx"));
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { defval: "" });
  };

  const parseCsv = async (file) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
    return lines.slice(1).map(line => {
      const cells = line.split(","); // simple parse — templates avoid commas in cells
      return Object.fromEntries(headers.map((h, i) => [h, (cells[i] || "").trim().replace(/^"|"$/g, "")]));
    });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const rows = f.name.toLowerCase().endsWith(".csv")
        ? await parseCsv(f)
        : await parseXlsx(f);
      if (!rows.length) { toast.error("File has no data rows"); setBusy(false); return; }
      const r = await api.post(`/${kind}/bulk-upload`, { rows });
      toast.success(`Created ${r.data.created} · Updated ${r.data.updated}${r.data.skipped ? ` · Skipped ${r.data.skipped}` : ""}`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} row(s) had issues — check console`);
      if (onDone) onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="flex items-start gap-2 flex-wrap" data-testid={`dim-bulk-${kind}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={downloadTemplate} className="gap-1.5" data-testid={`dim-tpl-${kind}`}>
          <Download className="w-3.5 h-3.5" /> Template
        </Button>
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`dim-upload-${kind}`}>
          {busy ? <FileSpreadsheet className="w-3.5 h-3.5 animate-pulse" /> : <Upload className="w-3.5 h-3.5" />}
          {busy ? "Uploading…" : `Bulk Upload ${label || "Dimensions"}`}
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid={`dim-file-${kind}`} />
      </div>
      <span className="text-[11px] text-muted-foreground">
        Excel/CSV · upserts by Name{(kind === "cost-centres" || kind === "dimensions") ? " · use the Parent Name column to nest a row under its parent Dimension (creates the hierarchy in one upload)." : "."}
      </span>
    </div>
  );
}
